import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Square, Trash2 } from 'lucide-react';
import { fmtHMS } from '@foodstyles/shared';
import { useNow } from '../../context/NowContext';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useToast } from '../../context/ToastContext';
import { useDeleteEntry, useStartTimer, useStopTimer } from '../../hooks/useEntries';
import { Btn, Card, inputCls } from '../../components/primitives';
import { TaskCombobox } from '../../components/TaskCombobox';
import { ApiError } from '../../lib/api';
import type { EntryDto } from '../../types/api';

interface Draft {
  description: string;
  projectId: string | null;
  taskId: string | null;
}

// The in-progress draft survives navigating away from the Tracker (per tab).
const DRAFT_KEY = 'fst-timer-draft';
interface StoredDraft extends Draft {
  runningId: string | null; // which running entry these edits belong to
}
function loadStoredDraft(): StoredDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as StoredDraft) : null;
  } catch {
    return null;
  }
}

export function TimerBar({ running, recent }: { running: EntryDto | null; recent: EntryDto[] }) {
  const now = useNow();
  const boot = useBootstrap();
  const toast = useToast();
  const start = useStartTimer();
  const stop = useStopTimer();
  const discard = useDeleteEntry();

  const [draft, setDraft] = useState<Draft>(() => {
    const s = loadStoredDraft();
    return s
      ? { description: s.description, projectId: s.projectId, taskId: s.taskId }
      : { description: '', projectId: null, taskId: null };
  });
  const [invalid, setInvalid] = useState(false);
  const [showAuto, setShowAuto] = useState(false);

  // Seed the draft from the running entry only when a DIFFERENT timer appears —
  // never over unsaved local edits for the same one (they'd be lost on remount).
  const seededFor = useRef<string | null>(loadStoredDraft()?.runningId ?? null);
  useEffect(() => {
    if (running && seededFor.current !== running.id) {
      seededFor.current = running.id;
      setDraft({ description: running.description, projectId: running.projectId, taskId: running.taskId });
    }
  }, [running?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the draft so page changes / reloads don't reset it.
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...draft, runningId: running?.id ?? null }));
    } catch {
      /* storage unavailable — drafts just won't persist */
    }
  }, [draft, running?.id]);

  const projects = boot.data?.projects ?? [];
  const tasks = boot.data?.tasks ?? [];

  // Latest 5 distinct finished entries; an empty input shows them all (recency),
  // typing narrows by contains-match.
  const suggestions = useMemo(() => {
    const needle = draft.description.trim().toLowerCase();
    const seen = new Set<string>();
    const out: EntryDto[] = [];
    for (const e of recent) {
      if (e.stop == null) continue;
      const d = e.description.trim();
      if (!d || seen.has(d.toLowerCase())) continue;
      if (!needle || d.toLowerCase().includes(needle)) {
        seen.add(d.toLowerCase());
        out.push(e);
      }
      if (out.length >= 5) break;
    }
    return out;
  }, [draft.description, recent]);

  const onStart = () => {
    start.mutate(
      { description: draft.description, projectId: draft.projectId, taskId: draft.taskId },
      {
        onSuccess: () => setDraft({ description: '', projectId: null, taskId: null }),
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not start the timer.'),
      },
    );
  };

  const onStop = () => {
    if (!draft.description.trim() || !draft.taskId) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    if (!running) return;
    stop.mutate(
      { id: running.id, description: draft.description, projectId: draft.projectId, taskId: draft.taskId },
      {
        // Fresh slate for the next entry once the timer stops.
        onSuccess: () => setDraft({ description: '', projectId: null, taskId: null }),
        onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not stop the timer.'),
      },
    );
  };

  // Discard a timer started by mistake: delete the running entry entirely (no
  // record kept). Destructive — the elapsed time is thrown away — so confirm first.
  const onDiscard = () => {
    if (!running) return;
    if (!confirm('Discard this running timer?\n\nThe elapsed time will be deleted and no entry is kept. This cannot be undone.')) {
      return;
    }
    setInvalid(false);
    discard.mutate(running.id, {
      onSuccess: () => setDraft({ description: '', projectId: null, taskId: null }),
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Could not discard the timer.'),
    });
  };

  return (
    <Card className={running ? 'border-[#FF5A3C]/30 bg-[#FF5A3C]/[0.03] dark:bg-[#FF5A3C]/10' : ''}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
        <div className="relative flex-1">
          <input
            className={inputCls}
            placeholder="What are you working on?"
            value={draft.description}
            onChange={(e) => {
              setDraft((d) => ({ ...d, description: e.target.value }));
              setShowAuto(true);
            }}
            onFocus={() => setShowAuto(true)}
            onBlur={() => setTimeout(() => setShowAuto(false), 150)}
          />
          {showAuto && suggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg">
              {suggestions.map((s) => (
                <button
                  key={s.id}
                  onMouseDown={() =>
                    setDraft({ description: s.description, projectId: s.projectId, taskId: s.taskId })
                  }
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <span className="truncate font-medium text-slate-800 dark:text-slate-100">{s.description}</span>
                  <span className="ml-auto shrink-0 text-xs text-slate-400">{s.taskName ?? s.projectName ?? ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:w-80">
          <TaskCombobox
            projects={projects}
            tasks={tasks}
            projectId={draft.projectId}
            taskId={draft.taskId}
            selectedLabel={running?.taskName ? [running.projectName, running.taskName].filter(Boolean).join(' · ') : undefined}
            invalid={invalid && !draft.taskId}
            onPick={({ projectId, taskId }) =>
              setDraft((d) => ({ ...d, projectId: projectId || null, taskId: taskId || null }))
            }
          />
        </div>

        <div className="flex items-center gap-3">
          {running && <span className="font-mono text-lg font-bold tabular-nums text-[#D8341A] dark:text-[#FF8A6B]">{fmtHMS(now - running.start)}</span>}
          {running ? (
            <>
              <Btn
                variant="ghost"
                size="lg"
                onClick={onDiscard}
                disabled={discard.isPending || stop.isPending}
                aria-label="Discard timer"
                title="Discard this timer — delete it with no entry kept"
                className="hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:text-rose-600 dark:hover:text-rose-400"
              >
                <Trash2 size={16} />
              </Btn>
              <Btn variant="danger" size="lg" className="min-w-[120px]" onClick={onStop} disabled={stop.isPending || discard.isPending}>
                <Square size={16} /> Stop
              </Btn>
            </>
          ) : (
            <Btn variant="primary" size="lg" className="min-w-[120px]" onClick={onStart} disabled={start.isPending}>
              <Play size={16} /> Start
            </Btn>
          )}
        </div>
      </div>
      {invalid && (
        <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">To stop, add a description and select a task. Task is mandatory.</p>
      )}
    </Card>
  );
}
